import {
  defineMessages,
  defineMessage,
  MessageDescriptor,
} from '@formatjs/intl';
import { validate as uuidValidate } from 'uuid';
import { flattenFormattedParts } from './utils';
import {
  getNumberParseInfo,
  TSdkIntlShape,
  TSdkIntlPart,
} from '../modules/LibraryIntl';
import type {
  TInstrumentDetails,
  TInstrumentDetailsCurrency,
  TInstrumentDetailsCreditNote,
  TInstrumentDetailsCoupon,
  TInstrumentDetailsLoyalty,
  TInstrumentDetailsReward,
  TInstrumentDetailsCasino,
  TInstrumentDetailsTicket,
  TCurrencyDetails,
  TFaceValue,
} from '../modules/TokenizedApiModelEntities';
import type { TStaticResources } from '../modules/StaticResources';

/** @category Contracts */
export const DEFAULT_CONTRACT_ACTION_FEE = 3000;

/** @category Instruments */
export const BSV_MINIMUM_TRANSFER_QUANTITY = 200;

/** @category Instruments */
export const CURRENCY_BSV = 'BSV';
/** @category Instruments */
export const CURRENCY_USD = 'USD';
/** @category Instruments */
export const CURRENCY_CAD = 'CAD';
/** @category Instruments */
export const CURRENCY_AUD = 'AUD';
/** @category Instruments */
export const CURRENCY_NZD = 'NZD';
/** @category Instruments */
export const CURRENCY_EUR = 'EUR';
/** @category Instruments */
export const CURRENCY_GBP = 'GBP';
/** @category Instruments */
export const CURRENCY_SGD = 'SGD';
/** @category Instruments */
export const CURRENCY_DEFAULT = CURRENCY_USD;

/** @category Instruments */
export const INSTRUMENT_TYPE_CURRENCY = 'CCY';
/** @category Instruments */
export const INSTRUMENT_TYPE_BSV = 'BSV';
/** @category Instruments */
export const INSTRUMENT_TYPE_UNKNOWN = '';
/** @category Instruments */
export const INSTRUMENT_TYPE_CREDIT_NOTE = 'CRN';
/** @category Instruments */
export const INSTRUMENT_TYPE_COUPON = 'COU';
/** @category Instruments */
export const INSTRUMENT_TYPE_LOYALTY = 'LOY';
/** @category Instruments */
export const INSTRUMENT_TYPE_REWARD = 'RPT';
/** @category Instruments */
export const INSTRUMENT_TYPE_CASINO = 'CHP';
/** @category Instruments */
export const INSTRUMENT_TYPE_TICKET = 'TIC';
/** @category Instruments */
export const INSTRUMENT_TYPE_VOUCHER = 'VOU';
/** @category Instruments */
export const INSTRUMENT_TYPE_FIXED_BOND = 'BFR';

/** @category Instruments */
export function isInstrumentDetailsCurrency(
  instrumentDetails?: TInstrumentDetails | null,
): instrumentDetails is TInstrumentDetailsCurrency {
  return instrumentDetails?.instrumentType === INSTRUMENT_TYPE_CURRENCY;
}
/** @category Instruments */
export function isInstrumentDetailsCreditNote(
  instrumentDetails?: TInstrumentDetails | null,
): instrumentDetails is TInstrumentDetailsCreditNote {
  return instrumentDetails?.instrumentType === INSTRUMENT_TYPE_CREDIT_NOTE;
}
/** @category Instruments */
export function isInstrumentDetailsCoupon(
  instrumentDetails?: TInstrumentDetails | null,
): instrumentDetails is TInstrumentDetailsCoupon {
  return instrumentDetails?.instrumentType === INSTRUMENT_TYPE_COUPON;
}
/** @category Instruments */
export function isInstrumentDetailsLoyalty(
  instrumentDetails?: TInstrumentDetails | null,
): instrumentDetails is TInstrumentDetailsLoyalty {
  return instrumentDetails?.instrumentType === INSTRUMENT_TYPE_LOYALTY;
}
/** @category Instruments */
export function isInstrumentDetailsReward(
  instrumentDetails?: TInstrumentDetails | null,
): instrumentDetails is TInstrumentDetailsReward {
  return instrumentDetails?.instrumentType === INSTRUMENT_TYPE_REWARD;
}
/** @category Instruments */
export function isInstrumentDetailsCasino(
  instrumentDetails?: TInstrumentDetails | null,
): instrumentDetails is TInstrumentDetailsCasino {
  return instrumentDetails?.instrumentType === INSTRUMENT_TYPE_CASINO;
}
/** @category Instruments */
export function isInstrumentDetailsTicket(
  instrumentDetails?: TInstrumentDetails | null,
): instrumentDetails is TInstrumentDetailsTicket {
  return instrumentDetails?.instrumentType === INSTRUMENT_TYPE_TICKET;
}

/** @category Instruments */
export const BLOCKCHAIN_CURRENCIES: Record<string, boolean | undefined> = {
  [CURRENCY_BSV]: true,
} as const;
/** @category Instruments */
export const DISPLAY_CURRENCIES = [
  CURRENCY_USD,
  CURRENCY_CAD,
  CURRENCY_AUD,
  CURRENCY_NZD,
  CURRENCY_EUR,
  CURRENCY_GBP,
  CURRENCY_SGD,
];

/** @category Instruments */
export const MAX_DISPLAY_PRECISION = 9;
/** @category Instruments */
export const MIN_EXCHANGE_RATE_PRECISION = 3;
/** @category Instruments */
export const CURRENCY_DEFAULT_PRECISION = 2;
/** @category Instruments */
export const defaultUnitPrecisionForCurrency = (
  currencyCode: string | null | undefined,
) =>
  currencyCode && BLOCKCHAIN_CURRENCIES[currencyCode]
    ? 8
    : CURRENCY_DEFAULT_PRECISION;

/** @category Instruments */
export interface TFormatAmountPartsOptions {
  isDelta?: boolean;
  hideCurrency?: boolean;
  showCurrencyCodeOnRight?: boolean;
  allowCurrencySymbolAndCode?: boolean;
  showDisplayCurrency?: boolean;
  showDisplayCurrencyWhenSame?: boolean;
  useCustomPrecisionForDisplayCurrency?: boolean;
  increasePrecisionForTinyNumbers?: boolean;
  showWhenZero?: boolean;
  showUnconvertibleAmountAsZero?: boolean;
  numberFormatOverrides?: Intl.NumberFormatOptions;
  splitLongFractions?: boolean;
  enforceCompleteFractionGroups?: boolean;
  typographicMinusSigns?: boolean;
  spaceAroundSigns?: boolean;
  forceSpaceAroundCurrency?: boolean;
}

/** @category Instruments */
export interface TFormatTokenQuantityPart {
  type: 'number';
  number: number;
  NumberFormatOptions: Intl.NumberFormatOptions;
  value: string;
}

/** @category Instruments */
export interface TFormatAmountExtendedPart {
  type:
    | 'tokenType'
    | 'space'
    | 'fractionLarge'
    | 'fractionMedium'
    | 'fractionSmall'
    | 'currency';
  subType?: 'symbolBefore' | 'codeBefore' | 'symbolAfter' | 'codeAfter';
  value: string;
}
/** @category Instruments */
export type TFormatAmountPart =
  | Intl.NumberFormatPart
  | TFormatAmountExtendedPart;
/** @category Instruments */
export interface TFormatAmountPartsResult {
  parts: TFormatAmountPart[];
  number: number;
  textOnly: string;
}

interface TNumberFormatOptionsInternal extends Intl.NumberFormatOptions {
  overridesForFormat?: Intl.NumberFormatOptions;
}

const FORMAT_INSTRUMENT_TYPE_DEFAULT = defineMessage({
  id: 'TokenizedSDK:instrument.type.default',
  description:
    'Identify the type of an instrument: not a currency, specific type unknown',
  defaultMessage: '{instrument_type} instrument',
});
const FORMAT_INSTRUMENT_TYPE: Record<string, MessageDescriptor> =
  defineMessages({
    [INSTRUMENT_TYPE_CURRENCY]: {
      id: 'TokenizedSDK:instrument.type.currency',
      description: 'Identify the type of an instrument: Currency',
      defaultMessage: 'Digital currency',
    },
    [INSTRUMENT_TYPE_BSV]: {
      id: 'TokenizedSDK:instrument.type.bsv',
      description: 'Identify the type of an instrument: Currency (BSV)',
      defaultMessage: 'Digital currency',
    },
    [INSTRUMENT_TYPE_CREDIT_NOTE]: {
      id: 'TokenizedSDK:instrument.type.creditNote',
      description: 'Identify the type of an instrument: Credit note',
      defaultMessage: 'Credit note',
    },
    [INSTRUMENT_TYPE_COUPON]: {
      id: 'TokenizedSDK:instrument.type.discountCoupon',
      description: 'Identify the type of an instrument: Discount coupon',
      defaultMessage: 'Discount coupon',
    },
    [INSTRUMENT_TYPE_LOYALTY]: {
      id: 'TokenizedSDK:instrument.type.loyaltyPoint',
      description: 'Identify the type of an instrument: Loyalty point',
      defaultMessage: 'Loyalty point',
    },
    [INSTRUMENT_TYPE_REWARD]: {
      id: 'TokenizedSDK:instrument.type.rewardPoint',
      description: 'Identify the type of an instrument: Reward point',
      defaultMessage: 'Reward point',
    },
    [INSTRUMENT_TYPE_CASINO]: {
      id: 'TokenizedSDK:instrument.type.casinoChip',
      description: 'Identify the type of an instrument: Casino chip',
      defaultMessage: 'Casino chip',
    },
    [INSTRUMENT_TYPE_TICKET]: {
      id: 'TokenizedSDK:instrument.type.eventTicket',
      description: 'Identify the type of an instrument: Event ticket',
      defaultMessage: 'Event ticket',
    },
    [INSTRUMENT_TYPE_FIXED_BOND]: {
      id: 'TokenizedSDK:instrument.type.fixedRateBond',
      description: 'Identify the type of an instrument: Fixed rate bond',
      defaultMessage: 'Fixed rate bond',
    },
    [INSTRUMENT_TYPE_VOUCHER]: {
      id: 'TokenizedSDK:instrument.type.voucher',
      description: 'Identify the type of an instrument: voucher',
      defaultMessage: 'Voucher',
    },
    [INSTRUMENT_TYPE_UNKNOWN]: {
      id: 'TokenizedSDK:instrument.type.unknown',
      description:
        'Identify the type of an instrument: not a currency, specific type unknown',
      defaultMessage: 'Instrument',
    },
  });
/** @category Instruments */
export const INSTRUMENT_TYPE_ORDER = Object.keys(FORMAT_INSTRUMENT_TYPE);

/**
 * A quantity of one of these “money” instruments directly represents an
 * amount of currency, and is always formatted as such (floating point
 * number with a currency symbol or code), rather than the integer
 * quantity of tokens.
 *
 * @category Instruments
 */
export const MONEY_INSTRUMENT_TYPES = [
  INSTRUMENT_TYPE_BSV,
  INSTRUMENT_TYPE_CURRENCY,
  INSTRUMENT_TYPE_CREDIT_NOTE,
] as const;
/** @category Instruments */
export type TMoneyInstrumentType = (typeof MONEY_INSTRUMENT_TYPES)[number];
/** @category Instruments */
export function isMoneyInstrumentType(
  instrumentType: unknown,
): instrumentType is TMoneyInstrumentType {
  return MONEY_INSTRUMENT_TYPES.some(
    (equivalentType) => instrumentType === equivalentType,
  );
}

const FORMAT_TOKEN_QUANTITY_DEFAULT = defineMessage({
  id: 'TokenizedSDK:token.quantity.default',
  description: 'Format a quantity of non-currency tokens, type not recognized',
  defaultMessage:
    '<number>{quantity, number}</number> <type>{instrument_type} {quantity, plural, one {gram} other {grams}}</type>',
});
const FORMAT_TOKEN_QUANTITY: Record<string, MessageDescriptor> = defineMessages(
  {
    [INSTRUMENT_TYPE_COUPON]: {
      id: 'TokenizedSDK:token.quantity.discountCoupon',
      description: 'Format a quantity of discount coupon tokens',
      defaultMessage:
        '<number>{quantity, number}</number> <type>{quantity, plural, one {coupon} other {coupons}}</type>',
    },
    [INSTRUMENT_TYPE_LOYALTY]: {
      id: 'TokenizedSDK:token.quantity.loyaltyPoint',
      description: 'Format a quantity of loyalty point tokens',
      defaultMessage:
        '<number>{quantity, number}</number> <type>{quantity, plural, one {point} other {points}}</type>',
    },
    [INSTRUMENT_TYPE_REWARD]: {
      id: 'TokenizedSDK:token.quantity.rewardPoint',
      description: 'Format a quantity of reward point tokens',
      defaultMessage:
        '<number>{quantity, number}</number> <type>{quantity, plural, one {point} other {points}}</type>',
    },
    [INSTRUMENT_TYPE_CASINO]: {
      id: 'TokenizedSDK:token.quantity.casinoChip',
      description: 'Format a quantity of casino chip tokens',
      defaultMessage:
        '<number>{quantity, number}</number> <type>{quantity, plural, one {chip} other {chips}}</type>',
    },
    [INSTRUMENT_TYPE_TICKET]: {
      id: 'TokenizedSDK:token.quantity.eventTicket',
      description: 'Format a quantity of event ticket tokens',
      defaultMessage:
        '<number>{quantity, number}</number> <type>{quantity, plural, one {ticket} other {tickets}}</type>',
    },
    [INSTRUMENT_TYPE_FIXED_BOND]: {
      id: 'TokenizedSDK:token.quantity.fixedRateBond',
      description: 'Format a quantity of Fixed rate bond tokens',
      defaultMessage:
        '<number>{quantity, number}</number> <type>{quantity, plural, one {bond} other {bonds}}</type>',
    },
    [INSTRUMENT_TYPE_UNKNOWN]: {
      id: 'TokenizedSDK:token.quantity.unknown',
      description:
        'Format a quantity of non-currency tokens, specific type unknown',
      defaultMessage:
        '<number>{quantity, number}</number> <type>{quantity, plural, one {gram} other {grams}}</type>',
    },
  },
);
const FORMAT_TOKEN_DELTA_DEFAULT = defineMessage({
  id: 'TokenizedSDK:token.delta.default',
  description:
    'Format a delta quantity of non-currency tokens, type not recognized',
  defaultMessage:
    '<number>{quantity, number, :: sign-always}</number> <type>{instrument_type} {quantity, plural, one {gram} other {grams}}</type>',
});
const FORMAT_TOKEN_DELTA: Record<string, MessageDescriptor> = defineMessages({
  [INSTRUMENT_TYPE_COUPON]: {
    id: 'TokenizedSDK:token.delta.discountCoupon',
    description: 'Format a delta quantity of discount coupon tokens',
    defaultMessage:
      '<number>{quantity, number, :: sign-always}</number> <type>{quantity, plural, one {coupon} other {coupons}}</type>',
  },
  [INSTRUMENT_TYPE_LOYALTY]: {
    id: 'TokenizedSDK:token.delta.loyaltyPoint',
    description: 'Format a delta quantity of loyalty point tokens',
    defaultMessage:
      '<number>{quantity, number, :: sign-always}</number> <type>{quantity, plural, one {point} other {points}}</type>',
  },
  [INSTRUMENT_TYPE_REWARD]: {
    id: 'TokenizedSDK:token.delta.rewardPoint',
    description: 'Format a delta quantity of reward point tokens',
    defaultMessage:
      '<number>{quantity, number, :: sign-always}</number> <type>{quantity, plural, one {point} other {points}}</type>',
  },
  [INSTRUMENT_TYPE_CASINO]: {
    id: 'TokenizedSDK:token.delta.casinoChip',
    description: 'Format a delta quantity of casino chip tokens',
    defaultMessage:
      '<number>{quantity, number, :: sign-always}</number> <type>{quantity, plural, one {chip} other {chips}}</type>',
  },
  [INSTRUMENT_TYPE_TICKET]: {
    id: 'TokenizedSDK:token.delta.eventTicket',
    description: 'Format a delta quantity of event ticket tokens',
    defaultMessage:
      '<number>{quantity, number, :: sign-always}</number> <type>{quantity, plural, one {ticket} other {tickets}}</type>',
  },
  [INSTRUMENT_TYPE_FIXED_BOND]: {
    id: 'TokenizedSDK:token.delta.fixedRateBond',
    description: 'Format a delta quantity of Fixed rate bond tokens',
    defaultMessage:
      '<number>{quantity, number, :: sign-always}</number> <type>{quantity, plural, one {bond} other {bonds}}</type>',
  },
  [INSTRUMENT_TYPE_UNKNOWN]: {
    id: 'TokenizedSDK:token.delta.unknown',
    description:
      'Format a delta quantity of non-currency tokens, specific type unknown',
    defaultMessage:
      '<number>{quantity, number, :: sign-always}</number> <type>{quantity, plural, one {gram} other {grams}}</type>',
  },
});
const FORMAT_TOKEN_UNITS_DEFAULT = defineMessage({
  id: 'TokenizedSDK:token.units.default',
  description:
    'Format units suffix for non-currency tokens, type not recognized',
  defaultMessage: '{type} {quantity, plural, one {gram} other {grams}}',
});
const FORMAT_TOKEN_UNITS: Record<string, MessageDescriptor> = defineMessages({
  [INSTRUMENT_TYPE_COUPON]: {
    id: 'TokenizedSDK:token.units.discountCoupon',
    description: 'Format units suffix for discount coupon tokens',
    defaultMessage: '{quantity, plural, one {coupon} other {coupons}}',
  },
  [INSTRUMENT_TYPE_LOYALTY]: {
    id: 'TokenizedSDK:token.units.loyaltyPoint',
    description: 'Format units suffix for loyalty point tokens',
    defaultMessage: '{quantity, plural, one {point} other {points}}',
  },
  [INSTRUMENT_TYPE_REWARD]: {
    id: 'TokenizedSDK:token.units.rewardPoint',
    description: 'Format units suffix for reward point tokens',
    defaultMessage: '{quantity, plural, one {point} other {points}}',
  },
  [INSTRUMENT_TYPE_CASINO]: {
    id: 'TokenizedSDK:token.units.casinoChip',
    description: 'Format units suffix for casino chip tokens',
    defaultMessage: '{quantity, plural, one {chip} other {chips}}',
  },
  [INSTRUMENT_TYPE_TICKET]: {
    id: 'TokenizedSDK:token.units.eventTicket',
    description: 'Format units suffix for event ticket tokens',
    defaultMessage: '{quantity, plural, one {ticket} other {tickets}}',
  },
  [INSTRUMENT_TYPE_FIXED_BOND]: {
    id: 'TokenizedSDK:token.units.fixedRateBond',
    description: 'Format units suffix for Fixed rate bond tokens',
    defaultMessage: '{quantity, plural, one {bond} other {bonds}}',
  },
  [INSTRUMENT_TYPE_UNKNOWN]: {
    id: 'TokenizedSDK:token.units.unknown',
    description:
      'Format units suffix for non-currency tokens, specific type unknown',
    defaultMessage: '{quantity, plural, one {gram} other {grams}}',
  },
});
/** @internal */
export const FORMAT_TOKEN_QUANTITY_LONG: Record<string, MessageDescriptor> =
  defineMessages({
    [INSTRUMENT_TYPE_COUPON]: {
      id: 'TokenizedSDK:token.quantity.long.discountCoupon',
      description: 'Format a quantity of a: Discount coupon',
      defaultMessage:
        '<number>{quantity, number}</number> <type>{quantity, plural, one {discount coupon} other {discount coupons}}</type>',
    },
    [INSTRUMENT_TYPE_LOYALTY]: {
      id: 'TokenizedSDK:token.quantity.long.loyaltyPoint',
      description: 'Format a quantity of a: Loyalty point',
      defaultMessage:
        '<number>{quantity, number}</number> <type>{quantity, plural, one {loyalty point} other {loyalty points}}</type>',
    },
    [INSTRUMENT_TYPE_REWARD]: {
      id: 'TokenizedSDK:token.quantity.long.rewardPoint',
      description: 'Format a quantity of a: Reward point',
      defaultMessage:
        '<number>{quantity, number}</number> <type>{quantity, plural, one {reward point} other {reward points}}</type>',
    },
    [INSTRUMENT_TYPE_CASINO]: {
      id: 'TokenizedSDK:token.quantity.long.casinoChip',
      description: 'Format a quantity of a: Casino chip',
      defaultMessage:
        '<number>{quantity, number}</number> <type>{quantity, plural, one {casino chip} other {casino chips}}</type>',
    },
    [INSTRUMENT_TYPE_TICKET]: {
      id: 'TokenizedSDK:token.quantity.long.eventTicket',
      description: 'Format a quantity of a: Event ticket',
      defaultMessage:
        '<number>{quantity, number}</number> <type>{quantity, plural, one {event ticket} other {event tickets}}</type>',
    },
    [INSTRUMENT_TYPE_FIXED_BOND]: {
      id: 'TokenizedSDK:token.quantity.long.fixedRateBond',
      description: 'Format a quantity of a: Fixed rate bond',
      defaultMessage:
        '<number>{quantity, number}</number> <type>{quantity, plural, one {fixed rate bond} other {fixed rate bonds}}</type>',
    },
  });

// Currrency name localization mechanism, currently only used to modify
// “Unites States dollar” to “US dollar”
const CURRENCY_NAMES: Record<string, MessageDescriptor> = defineMessages({
  'Bitcoin SV': {
    id: 'TokenizedSDK:currency.name.BSV',
    description: 'Currency name',
    defaultMessage: 'Bitcoin SV',
  },
  'Pound sterling': {
    id: 'TokenizedSDK:currency.name.GBP',
    description: 'Currency name',
    defaultMessage: 'Pound sterling',
  },
  'Australian dollar': {
    id: 'TokenizedSDK:currency.name.AUD',
    description: 'Currency name',
    defaultMessage: 'Australian dollar',
  },
  'United States dollar': {
    id: 'TokenizedSDK:currency.name.USD',
    description: 'Currency name',
    defaultMessage: 'US dollar',
  },
});

/** @internal */
export function formatInstrumentTypeName(
  instrumentType: string | null | undefined,
  intl: TSdkIntlShape,
) {
  if (
    !instrumentType ||
    typeof instrumentType !== 'string' ||
    instrumentType.length !== 3
  ) {
    return intl.formatMessage(FORMAT_INSTRUMENT_TYPE[INSTRUMENT_TYPE_UNKNOWN]);
  }
  if (FORMAT_INSTRUMENT_TYPE[instrumentType]) {
    return intl.formatMessage(FORMAT_INSTRUMENT_TYPE[instrumentType]);
  }
  return intl.formatMessage(FORMAT_INSTRUMENT_TYPE_DEFAULT, {
    instrument_type: instrumentType,
  });
}

const spaceCharacter = ({
  nonBreaking,
  narrow,
}: { nonBreaking?: boolean; narrow?: boolean } = {}) => {
  if (nonBreaking) {
    return narrow ? '\u202f' : '\xa0';
  } else {
    return narrow ? '\u2009' : ' ';
  }
};
const matchWhitespace = /^\s+$/s;

// For some types of instrument, we want to construct a name
// dynamically from its definition fields
/** @internal */
export function formatInstrumentName({
  intl,
  instrumentDetails,
  currencyDetails,
  fallbackName,
  noFallback,
}: {
  intl: TSdkIntlShape;
  instrumentDetails?: TInstrumentDetails | null;
  currencyDetails?: TCurrencyDetails | null;
  fallbackName?: string;
  noFallback?: boolean;
}): string | null {
  let instrumentType = instrumentDetails?.instrumentType;
  if (!instrumentType && currencyDetails) {
    instrumentType = INSTRUMENT_TYPE_CURRENCY;
  }
  if (instrumentType === INSTRUMENT_TYPE_CURRENCY) {
    if (currencyDetails?.name) {
      const mappedName = CURRENCY_NAMES[currencyDetails.name];
      if (mappedName) {
        return intl.formatMessage(mappedName);
      }
      return currencyDetails.name;
    }
    if (currencyDetails?.currencyCode) {
      return currencyDetails.currencyCode;
    }
  } else if (isInstrumentDetailsCreditNote(instrumentDetails)) {
    let currencyName;
    if (currencyDetails?.name) {
      const mappedName = CURRENCY_NAMES[currencyDetails.name];
      if (mappedName) {
        currencyName = intl.formatMessage(mappedName);
      } else {
        currencyName = currencyDetails.name;
      }
    }
    if (!currencyName && currencyDetails?.currencyCode) {
      currencyName = currencyDetails.currencyCode;
    }
    if (currencyName) {
      return intl.formatMessage(
        {
          id: 'TokenizedSDK:instrument.name.creditNote',
          description:
            'Generated instrument name for credit notes (currency is "US dollars", "Pounds sterling", etc)',
          defaultMessage: '{currency} credit',
        },
        { currency: currencyName },
      );
    }
    if (instrumentDetails?.instrumentTypeSpecifics?.name) {
      return instrumentDetails.instrumentTypeSpecifics.name;
    }
  } else if (isInstrumentDetailsCoupon(instrumentDetails)) {
    if (instrumentDetails?.instrumentTypeSpecifics?.couponName) {
      return instrumentDetails.instrumentTypeSpecifics.couponName;
    }
  } else if (isInstrumentDetailsLoyalty(instrumentDetails)) {
    if (instrumentDetails?.instrumentTypeSpecifics?.programName) {
      return instrumentDetails.instrumentTypeSpecifics.programName;
    }
  } else if (isInstrumentDetailsReward(instrumentDetails)) {
    if (instrumentDetails?.instrumentTypeSpecifics?.programName) {
      return instrumentDetails.instrumentTypeSpecifics.programName;
    }
  } else if (isInstrumentDetailsCasino(instrumentDetails)) {
    if (instrumentDetails?.instrumentTypeSpecifics?.casinoName) {
      if (
        instrumentDetails?.instrumentTypeSpecifics?.faceValue &&
        instrumentDetails.instrumentTypeSpecifics.faceValue.currency &&
        instrumentDetails.instrumentTypeSpecifics.faceValue.units &&
        instrumentDetails.instrumentTypeSpecifics.faceValue.units > 0 &&
        typeof instrumentDetails.instrumentTypeSpecifics.faceValue
          .unitPrecision === 'number' &&
        Number.isInteger(
          instrumentDetails.instrumentTypeSpecifics.faceValue.unitPrecision,
        )
      ) {
        const amount =
          instrumentDetails.instrumentTypeSpecifics.faceValue.units /
          10 **
            instrumentDetails.instrumentTypeSpecifics.faceValue.unitPrecision;
        let precision = 0;
        if (!Number.isInteger(amount)) {
          precision = Math.max(
            2,
            instrumentDetails.instrumentTypeSpecifics.faceValue.unitPrecision,
          );
        }
        return `${
          instrumentDetails?.instrumentTypeSpecifics.casinoName
        } (${intl.formatNumber(amount, {
          style: 'currency',
          currency:
            instrumentDetails.instrumentTypeSpecifics.faceValue.currency,
          minimumFractionDigits: precision,
          maximumFractionDigits: precision,
        })})`;
      }
      return instrumentDetails.instrumentTypeSpecifics.casinoName;
    }
  } else if (isInstrumentDetailsTicket(instrumentDetails)) {
    if (instrumentDetails?.instrumentTypeSpecifics?.eventName) {
      return instrumentDetails?.instrumentTypeSpecifics.eventName;
    }
  }
  return (
    fallbackName ||
    (noFallback ? null : formatInstrumentTypeName(instrumentType, intl))
  );
}

export interface TContract {
  details?: {
    contractExpiration?: number;
  };
  contractAddress: string;
  name: string;
}

/** @internal */
export function isInstrumentExpired(
  instrumentDetails: TInstrumentDetails | null | undefined,
  contract?: TContract,
) {
  const now = Date.now();
  // Currently, most supported event types have an ExpirationTimestamp payload field
  let expirationTimestamp = instrumentDetails?.instrumentTypeSpecifics
    ?.expirationTimestamp as number | undefined;
  if (isInstrumentDetailsTicket(instrumentDetails)) {
    // Special-case rules for expiry of event tickets
    if (
      instrumentDetails?.instrumentTypeSpecifics &&
      !instrumentDetails.instrumentTypeSpecifics.eventStartTimestamp
    ) {
      // Events with no timestamp count as expired
      return true;
    }
    expirationTimestamp =
      instrumentDetails?.instrumentTypeSpecifics?.eventEndTimestamp;
  }
  if (expirationTimestamp && expirationTimestamp <= now) {
    return true;
  }
  if (contract?.details?.contractExpiration) {
    // Instrument contract expiry check
    if (contract.details.contractExpiration <= now) {
      return true;
    }
  }
  // Default to assuming instruments haven’t expired
  return false;
}

/** @category Instruments */
export interface TInstrumentOptions {
  instrumentId?: string;
  draftInstrumentId?: string;
  instrumentType?: string;
  currencyCode?: string;
  amount?: number;
  quantity?: number;
  precision?: number;
  customPrecisionFromAmount?: boolean;
  roundAmountToQuantity?: boolean;
}
/** @category Instruments */
export type TInstrumentDescription =
  | TInstrumentOptions
  | Instrument
  | string
  | undefined;

/** @category Instruments */
export interface TInstrumentQuantityOptions {
  quantity?: number;
  amount?: number;
  precision?: number;
  customPrecisionFromAmount?: boolean;
  roundAmountToQuantity?: boolean;
  calculatedBy?: unknown;
}

/** @category Instruments */
export class Instrument {
  instrumentId: string | undefined;
  isDraft: boolean;
  instrumentType: string | undefined;
  currencyCode: string | undefined;

  /** @internal */
  #specifiedQuantity: number | undefined;
  /** @internal */
  #specifiedAmount: number | undefined;
  /** @internal */
  #specifiedPrecision: number | undefined;
  /** @internal */
  #unparsedInput: string | undefined;
  /** @internal */
  #calculatedBy: unknown;
  /** @internal */
  #displayCurrencyCode: string | undefined;

  /** @internal */
  instrumentDetails?: TInstrumentDetails;
  /** @internal */
  currencyDetails?: TCurrencyDetails;
  /** @internal */
  currenciesStaticResources?: TStaticResources['currencies'];
  /** @internal */
  exchangeRate?: number;
  /** @internal */
  intl?: TSdkIntlShape;

  static isInstrument(instrument: unknown): instrument is Instrument {
    return instrument instanceof Instrument;
  }

  constructor(options: TInstrumentDescription) {
    this.instrumentId = undefined;
    this.isDraft = false;
    this.instrumentType = INSTRUMENT_TYPE_UNKNOWN;
    if (options) {
      if (options instanceof Instrument) {
        // Duplicate an existing Instrument object
        Object.assign(this, options);
        this.#specifiedQuantity = options.#specifiedQuantity;
        this.#specifiedAmount = options.#specifiedAmount;
        this.#specifiedPrecision = options.#specifiedPrecision;
        this.#unparsedInput = options.#unparsedInput;
        this.#calculatedBy = options.#calculatedBy;
        this.#displayCurrencyCode = options.#displayCurrencyCode;
      } else if (typeof options === 'string') {
        // Create an instrument from an ID (can be instrument ID, instrument type, or currency)
        const id = options;
        if (typeof id !== 'string' || id.length < 3) {
          throw new TypeError(
            'Invalid new Instrument(): ID must be a string of length >= 3',
          );
        }
        if (id.length === 3) {
          if (id === CURRENCY_BSV) {
            this.instrumentId = CURRENCY_BSV;
            this.instrumentType = INSTRUMENT_TYPE_BSV;
            this.currencyCode = CURRENCY_BSV;
          } else if (FORMAT_INSTRUMENT_TYPE[id]) {
            this.instrumentType = id;
          } else {
            this.instrumentType = INSTRUMENT_TYPE_CURRENCY;
            this.currencyCode = id;
          }
        } else {
          this.instrumentId = id;
          if (uuidValidate(id)) {
            this.isDraft = true;
          } else {
            this.instrumentType = id.slice(0, 3);
          }
        }
      } else {
        // Construct from options object
        if (typeof options !== 'object') {
          throw new TypeError(
            'Invalid new Instrument(): Specify a string ID/code or options object',
          );
        }
        const {
          draftInstrumentId,
          instrumentId,
          instrumentType,
          currencyCode,
          amount,
          quantity,
          precision,
          customPrecisionFromAmount,
          roundAmountToQuantity,
        } = options;
        if (draftInstrumentId && typeof draftInstrumentId === 'string') {
          this.instrumentId = draftInstrumentId;
          this.isDraft = true;
          this.instrumentType = instrumentType;
        } else if (instrumentId) {
          if (typeof instrumentId !== 'string' || instrumentId.length < 3) {
            throw new TypeError(
              'Invalid new Instrument(): instrumentId must be a string of length >= 3',
            );
          }
          if (instrumentId === CURRENCY_BSV) {
            this.instrumentId = CURRENCY_BSV;
            this.instrumentType = INSTRUMENT_TYPE_BSV;
            this.currencyCode = CURRENCY_BSV;
          } else {
            this.instrumentId = instrumentId;
            if (instrumentId.length > 3 && uuidValidate(instrumentId)) {
              this.isDraft = true;
            } else {
              this.instrumentType = instrumentId.slice(0, 3);
            }
          }
        } else if (instrumentType) {
          if (
            instrumentType !== INSTRUMENT_TYPE_UNKNOWN &&
            (typeof instrumentType !== 'string' || instrumentType.length !== 3)
          ) {
            throw new TypeError(
              'Invalid new Instrument(): instrumentType must be a string of length 3',
            );
          }
          this.instrumentType = instrumentType;
          if (instrumentType === INSTRUMENT_TYPE_CURRENCY && currencyCode) {
            if (typeof currencyCode !== 'string' || currencyCode.length !== 3) {
              throw new TypeError(
                'Invalid new Instrument(): currencyCode must be a string of length 3',
              );
            }
            this.currencyCode = currencyCode;
            if (currencyCode === CURRENCY_BSV) {
              this.instrumentId = CURRENCY_BSV;
              this.instrumentType = INSTRUMENT_TYPE_BSV;
            }
          }
        } else if (currencyCode) {
          if (typeof currencyCode !== 'string' || currencyCode.length !== 3) {
            throw new TypeError(
              'Invalid new Instrument(): currencyCode must be a string of length 3',
            );
          }
          this.instrumentType = INSTRUMENT_TYPE_CURRENCY;
          this.currencyCode = currencyCode;
          if (currencyCode === CURRENCY_BSV) {
            this.instrumentId = CURRENCY_BSV;
            this.instrumentType = INSTRUMENT_TYPE_BSV;
          }
        }

        this.#setQuantity({
          quantity,
          amount,
          precision,
          customPrecisionFromAmount,
          roundAmountToQuantity,
        });
      }
    }
  }

  /** @internal */
  #setQuantity({
    quantity,
    amount,
    precision,
    customPrecisionFromAmount,
    roundAmountToQuantity,
    calculatedBy,
  }: TInstrumentQuantityOptions = {}) {
    this.#specifiedQuantity = undefined;
    this.#specifiedAmount = undefined;
    this.#specifiedPrecision = undefined;
    this.#calculatedBy = calculatedBy || null;

    if (
      (typeof quantity === 'number' || typeof quantity === 'string') &&
      !isNaN(quantity)
    ) {
      this.#specifiedQuantity = Math.round(quantity);
      if (
        (typeof precision === 'number' || typeof precision === 'string') &&
        !isNaN(precision)
      ) {
        if (!this.isMoney && precision !== 0) {
          throw new TypeError(
            `Invalid new Instrument(): precision must be zero for non-currency (token) quantities (specified ${precision})`,
          );
        }
        if (precision < 0) {
          throw new TypeError(
            `Invalid new Instrument(): precision must >= zero (specified ${precision})`,
          );
        }
        if (precision > MAX_DISPLAY_PRECISION) {
          throw new TypeError(
            `Invalid new Instrument(): precision must <= ${MAX_DISPLAY_PRECISION} (specified ${precision})`,
          );
        }
        this.#specifiedPrecision = Math.round(precision);
      }
    } else if (
      (typeof amount === 'number' || typeof amount === 'string') &&
      !isNaN(amount)
    ) {
      this.#specifiedAmount = Number(amount);
      if (customPrecisionFromAmount && this.isMoney) {
        let precision = MAX_DISPLAY_PRECISION;
        while (precision > CURRENCY_DEFAULT_PRECISION) {
          const digit =
            Math.round(this.#specifiedAmount * 10 ** precision) % 10;
          if (digit > 0) {
            break;
          }
          precision -= 1;
        }
        this.#specifiedPrecision = precision;
      } else if (
        (typeof precision === 'number' || typeof precision === 'string') &&
        !isNaN(precision)
      ) {
        if (!this.isMoney && precision !== 0) {
          throw new TypeError(
            `Invalid new Instrument(): precision must be zero for non-currency (token) quantities (specified ${precision})`,
          );
        }
        if (precision < 0) {
          throw new TypeError(
            `Invalid new Instrument(): precision must >= zero (specified ${precision})`,
          );
        }
        if (precision > MAX_DISPLAY_PRECISION) {
          throw new TypeError(
            `Invalid new Instrument(): precision must <= ${MAX_DISPLAY_PRECISION} (specified ${precision})`,
          );
        }
        this.#specifiedPrecision = Math.round(precision);
      }
      if (roundAmountToQuantity) {
        this.#specifiedQuantity = this.quantity;
        this.#specifiedAmount = undefined;
      }
    }
  }

  /** @internal */
  withIntl(intl: TSdkIntlShape | null | undefined) {
    if (intl) {
      const copy = new Instrument(this);
      copy.intl = intl;
      return copy;
    }
    return this;
  }

  /** @internal */
  get locale() {
    return this.intl?.locale || undefined;
  }

  /** @internal */
  get instrumentIdToQuery() {
    // Exceptions:
    //   * If instrumentDetails has been set then this Instrument is bound
    //     to a query and there’s no need to query again.
    //   * Instrument details query fails for “BSV”, so don’t bother
    //   * For a draft instrument, the contract has to be queried instead
    if (
      this.instrumentId &&
      !this.isDraft &&
      this.instrumentId?.length > 3 &&
      !this.instrumentDetails
    ) {
      return this.instrumentId;
    }
    return null;
  }

  /** @internal */
  withInstrumentDetails(
    instrumentDetails: TInstrumentDetails | null | undefined,
  ) {
    if (instrumentDetails) {
      const copy = new Instrument(this);
      copy.instrumentDetails = instrumentDetails;
      if (instrumentDetails?.instrumentId) {
        // For completeness (shouldn’t be necessary)
        copy.instrumentId = instrumentDetails.instrumentId;
      }
      if (instrumentDetails?.instrumentType) {
        copy.instrumentType = instrumentDetails.instrumentType;
        if (
          isInstrumentDetailsCurrency(instrumentDetails) &&
          instrumentDetails.instrumentTypeSpecifics?.currencyCode
        ) {
          copy.currencyCode =
            instrumentDetails.instrumentTypeSpecifics.currencyCode;
        } else if (
          isInstrumentDetailsCreditNote(instrumentDetails) &&
          instrumentDetails.instrumentTypeSpecifics?.faceValue?.currency &&
          !copy.currencyCode
        ) {
          // Treat a credit note Instrument like a currency Instrument
          copy.currencyCode =
            instrumentDetails.instrumentTypeSpecifics.faceValue.currency;
        }
      }
      // We might have a new currency code, so update currency details
      return copy.withCurrencyDetailsFromStaticData();
    }
    return this;
  }

  /** @internal */
  get currencyIdToQuery() {
    // If currencyDetails has been set then this Instrument is bound
    // to a query and there’s no need to query again.
    if (!this.currencyDetails) {
      if (this.isCurrency) {
        if (this.instrumentId) {
          return this.instrumentId;
        }
        if (this.currencyCode) {
          return this.currencyCode;
        }
      } else if (this.currencyCode) {
        // For currency-equivalent instruments like credit notes,
        // withInstrumentDetails will set the currencyCode.
        return this.currencyCode;
      }
    }
    return null;
  }

  /** @internal */
  withCurrencyDetailsFromStaticData() {
    if (
      this.currenciesStaticResources &&
      this.currencyCode &&
      this.currencyDetails?.currencyCode !== this.currencyCode
    ) {
      const resourceData = this.currenciesStaticResources.get(
        this.currencyCode,
      );
      if (resourceData?.label) {
        const copy = new Instrument(this);
        copy.currencyDetails = {
          currencyCode: this.currencyCode,
          name: resourceData.label,
          symbol: resourceData.metadata?.symbol,
          unitPrecision: resourceData.metadata?.precision,
        };
        return copy;
      }
    }
    return this;
  }

  /** @internal */
  withCurrenciesStaticResources(
    currencies: TStaticResources['currencies'] | null | undefined,
  ) {
    if (currencies && !this.currenciesStaticResources) {
      const copy = new Instrument(this);
      copy.currenciesStaticResources = currencies;
      return copy.withCurrencyDetailsFromStaticData();
    }
    return this;
  }

  /** @internal */
  get exchangeRateToQuery() {
    // If exchangeRate has been set then this Instrument is bound
    // to a query and there’s no need to query again.
    if (!this.exchangeRate) {
      return this.instrumentCurrencyCode ?? null;
    }
    return null;
  }

  /** @internal */
  withDisplayCurrencyRate(
    displayCurrencyCode: string | null | undefined,
    exchangeRate: number | null | undefined,
  ) {
    // Important behaviors achieved by the logic below:
    // - If displayCurrencyCode is null/undefined (happens during async loading), do nothing
    // - If exchangeRate is null/undefined (happens while fetching exchange rate), or invalid
    //   (zero or negative), do nothing unless the currency has changed, in which case clear
    //   any previous rate
    // - If the exact same currency and rate are set again, do nothing (don’t create a new object)
    if (
      typeof displayCurrencyCode === 'string' &&
      displayCurrencyCode.length === 3 &&
      (displayCurrencyCode !== this.#displayCurrencyCode ||
        (typeof exchangeRate === 'number' &&
          exchangeRate > 0 &&
          exchangeRate !== this.exchangeRate))
    ) {
      const copy = new Instrument(this);
      copy.#displayCurrencyCode = displayCurrencyCode;
      if (typeof exchangeRate === 'number' && exchangeRate > 0) {
        copy.exchangeRate = exchangeRate;
      } else {
        copy.exchangeRate = undefined;
      }
      return copy;
    }
    return this;
  }

  withQuantity(options: TInstrumentQuantityOptions) {
    const copy = new Instrument(this);
    copy.#setQuantity(options);
    return copy;
  }

  get hasDetails() {
    // Notes:
    // - BSV has no instrument details available
    // - Currencies are allowed to have no instrument details
    //   (no CCY instrument posted on-chain)
    return (
      !!this.instrumentDetails || this.isBsv || this.hasDetailsIncludingCurrency
    );
  }

  get hasDetailsIncludingCurrency() {
    if (this.isBsv) {
      // BSV is a special case. There are no instrument details
      // available, but currency details and exchange rate are required.
      return !!this.currencyDetails && !!this.exchangeRate;
    }
    if (this.isCurrency) {
      // Currently if the instrumentType is INSTRUMENT_TYPE_CURRENCY
      // it might or might not have a actual CCY instrument
      // associated with it, therefore the instrumentId is optional,
      // but currency details and exchange rate must be available.
      return !!this.currencyDetails && !!this.exchangeRate;
    }
    if (this.isMoney) {
      // For currency-equivalent instruments like credit notes,
      // currencyDetails and exchangeRate are required in addition
      // to the instrument details data.
      return (
        !!this.instrumentDetails &&
        !!this.currencyDetails &&
        !!this.exchangeRate
      );
    }
    if (this.instrumentCurrencyCode) {
      // For tokens with a face value, we need an exchange rate
      return !!this.instrumentDetails && !!this.exchangeRate;
    }
    // For all other instrument types, we get everything we need
    // from the instrument details.
    return !!this.instrumentDetails;
  }

  get iconCode() {
    return this.isMoney ? this.currencyCode : this.instrumentType;
  }

  get isCurrency() {
    return this.isBsv || this.instrumentType === INSTRUMENT_TYPE_CURRENCY;
  }

  get isMoney() {
    return isMoneyInstrumentType(this.instrumentType);
  }

  get isBsv() {
    return this.instrumentId === CURRENCY_BSV;
  }

  isLiabilityForEntity(entityId: string | null | undefined) {
    return (
      !!this.instrumentDetails?.issuerId &&
      this.instrumentDetails.issuerId === entityId
    );
  }

  isExpired(contract?: TContract) {
    return isInstrumentExpired(this.instrumentDetails, contract);
  }

  get instrumentCurrencyCode() {
    if (this.isMoney) {
      return this.currencyCode;
    } else {
      const tokenFaceValue = this.instrumentDetails?.instrumentTypeSpecifics
        ?.faceValue as TFaceValue | undefined;
      const tokenFaceValueCurrencyCode = tokenFaceValue?.currency;
      if (
        typeof tokenFaceValueCurrencyCode === 'string' &&
        tokenFaceValueCurrencyCode.length === 3
      ) {
        return tokenFaceValueCurrencyCode;
      }
    }
    return null;
  }

  get hasQuantity() {
    return (
      (typeof this.#specifiedQuantity === 'number' &&
        !isNaN(this.#specifiedQuantity)) ||
      (typeof this.#specifiedAmount === 'number' &&
        !isNaN(this.#specifiedAmount))
    );
  }

  /**
   * The default precision for the instrument:
   *
   * - For currencies, this is the precision defined in the static resources
   * - For money tokens like credit notes, this is the precision defined by
   *   the issuer in the instrument contract
   * - If the above info is unavailable, the default currency precision (2,
   *   or 8 for BSV) is used.
   * - For other tokens, this is zero
   */
  get defaultPrecision() {
    if (this.isMoney) {
      if (
        isInstrumentDetailsCreditNote(this.instrumentDetails) &&
        typeof this.instrumentDetails?.instrumentTypeSpecifics?.faceValue
          ?.unitPrecision === 'number'
      ) {
        const precision = Math.round(
          this.instrumentDetails?.instrumentTypeSpecifics?.faceValue
            ?.unitPrecision,
        );
        if (
          !isNaN(precision) &&
          precision >= 0 &&
          precision <= MAX_DISPLAY_PRECISION
        ) {
          return precision;
        }
      }
      if (typeof this.currencyDetails?.unitPrecision === 'number') {
        const precision = Math.round(this.currencyDetails?.unitPrecision);
        if (
          !isNaN(precision) &&
          precision >= 0 &&
          precision <= MAX_DISPLAY_PRECISION
        ) {
          return precision;
        }
      }
      return defaultUnitPrecisionForCurrency(this.currencyCode);
    }
    return 0;
  }

  get precision() {
    if (this.isMoney) {
      if (typeof this.#specifiedPrecision === 'number') {
        return this.#specifiedPrecision;
      }
    }
    return this.defaultPrecision;
  }

  get hasCustomPrecision() {
    return this.precision !== this.defaultPrecision;
  }

  get quantity() {
    let quantity = 0;
    if (typeof this.#specifiedQuantity === 'number') {
      quantity = this.#specifiedQuantity;
    } else if (typeof this.#specifiedAmount === 'number') {
      quantity = Math.round(this.#specifiedAmount * 10 ** this.precision);
    }
    if (isNaN(quantity)) {
      return 0;
    }
    return quantity;
  }

  static toQuantity(instrument: unknown) {
    if (Instrument.isInstrument(instrument) && instrument.hasQuantity) {
      return instrument.quantity;
    }
    return instrument;
  }

  get amount() {
    let amount = 0;
    if (typeof this.#specifiedAmount === 'number') {
      amount = this.#specifiedAmount;
    } else if (typeof this.#specifiedQuantity === 'number') {
      amount = this.#specifiedQuantity / 10 ** this.precision;
    }
    if (isNaN(amount)) {
      return 0;
    }
    return amount;
  }

  get calculatedBy() {
    return this.#calculatedBy;
  }

  get displayCurrencyCode() {
    return this.#displayCurrencyCode || CURRENCY_DEFAULT;
  }

  withCalculatedByCleared() {
    const copy = new Instrument(this);
    copy.#calculatedBy = null;
    return copy;
  }

  equals(otherInstrument: unknown) {
    return (
      Instrument.isInstrument(otherInstrument) &&
      this.instrumentId === otherInstrument.instrumentId &&
      this.instrumentType === otherInstrument.instrumentType &&
      this.currencyCode === otherInstrument.currencyCode &&
      this.amount === otherInstrument.amount &&
      this.calculatedBy === otherInstrument.calculatedBy
    );
  }

  static isEqual(a: unknown, b: unknown) {
    return a === b || (Instrument.isInstrument(a) && a.equals(b));
  }

  static toAmount(instrument: unknown) {
    if (Instrument.isInstrument(instrument) && instrument.hasQuantity) {
      return instrument.amount;
    }
    return instrument;
  }

  /**
   * Resets the precision back to the default for the instrument,
   * and rounds the amount to an integer quantity at that precision.
   */
  toRoundedQuantity() {
    if (typeof this.#specifiedPrecision === 'number') {
      // We need to convert back from the amount if
      // the current quantity is set to a custom precision
      return this.withQuantity({
        amount: this.amount,
        roundAmountToQuantity: true,
        calculatedBy: this.calculatedBy,
      });
    }
    return this.withQuantity({
      quantity: this.quantity,
      calculatedBy: this.calculatedBy,
    });
  }

  /**
   * Note that the Instrument returned by this method will not have
   * an {@linkcode Instrument.instrumentId | instrumentId}, just a
   * {@linkcode Instrument.currencyCode | currencyCode}, so it will work for
   * number conversions and formatting, but you need to pass it through
   * {@linkcode useInstrumentWithDetails} again to get the full details.
   */
  toCurrencyAmount({
    withDefaultPrecision,
  }: { withDefaultPrecision?: boolean } = {}) {
    if (this.isCurrency) {
      // Note that this special case only applies to true currencies,
      // not currency-equivalent instruments like credit notes – we
      // want them to be converted to their represented currency
      // like any other token with a face value.
      if (!this.currencyCode) {
        return null;
      }
      if (this.hasCustomPrecision && withDefaultPrecision) {
        // Sometimes we want the formatted result to always
        // have the default precision of the currency. Note that
        // the fractional parts of the amount are preserved.
        return this.withQuantity({
          amount: this.amount,
          calculatedBy: this.calculatedBy,
        });
      }
      // Already a currency amount
      return this;
    }
    // Some types of non-currency instrument (user-defined tokens) have
    // specified face values, and some don’t
    const tokenFaceValue = this.instrumentDetails?.instrumentTypeSpecifics
      ?.faceValue as TFaceValue | undefined;
    const tokenFaceValueCurrencyCode = tokenFaceValue?.currency;
    if (
      typeof tokenFaceValueCurrencyCode === 'string' &&
      tokenFaceValueCurrencyCode.length === 3
    ) {
      let tokenFaceValueQuantity;
      if (typeof tokenFaceValue?.units === 'number') {
        tokenFaceValueQuantity = Math.round(tokenFaceValue?.units);
        if (isNaN(tokenFaceValueQuantity)) {
          tokenFaceValueQuantity = 0;
        }
      }
      let tokenFaceValuePrecision;
      if (typeof tokenFaceValue?.unitPrecision === 'number') {
        tokenFaceValuePrecision = Math.round(tokenFaceValue?.unitPrecision);
        if (
          isNaN(tokenFaceValuePrecision) ||
          tokenFaceValuePrecision < 0 ||
          tokenFaceValuePrecision > MAX_DISPLAY_PRECISION
        ) {
          tokenFaceValuePrecision = defaultUnitPrecisionForCurrency(
            tokenFaceValueCurrencyCode,
          );
        }
      }
      return new Instrument({
        currencyCode: tokenFaceValueCurrencyCode,
        quantity:
          typeof tokenFaceValueQuantity === 'number'
            ? this.quantity * tokenFaceValueQuantity
            : undefined,
        precision: tokenFaceValuePrecision,
        calculatedBy: this.calculatedBy,
      })
        .withIntl(this.intl)
        .withCurrenciesStaticResources(this.currenciesStaticResources)
        .withDisplayCurrencyRate(this.displayCurrencyCode, this.exchangeRate);
    }
    // This instrument can’t be converted to a currency amount
    return null;
  }

  /**
   * Note that the Instrument returned by this method will not have
   * an {@linkcode Instrument.instrumentId | instrumentId}, just a
   * {@linkcode Instrument.currencyCode | currencyCode}, so it will work for
   * number conversions and formatting, but you need to pass it through
   * {@linkcode useInstrumentWithDetails} again to get the full details.
   */
  toDisplayCurrencyAmount({
    showUnconvertibleAmountAsZero,
  }: {
    showUnconvertibleAmountAsZero?: boolean;
  } = {}) {
    if (!this.displayCurrencyCode) {
      console.error("Can't convert to display currency amount without a code");
      return null;
    }
    const currencyAmount = this.toCurrencyAmount();
    if (currencyAmount) {
      if (
        this.displayCurrencyCode === currencyAmount.currencyCode &&
        !currencyAmount.hasCustomPrecision
      ) {
        return currencyAmount;
      }
      if (typeof currencyAmount.exchangeRate === 'number') {
        return new Instrument({
          currencyCode: currencyAmount.displayCurrencyCode,
          amount: currencyAmount.amount * currencyAmount.exchangeRate,
          calculatedBy: this.calculatedBy,
        })
          .withIntl(this.intl)
          .withCurrenciesStaticResources(this.currenciesStaticResources)
          .withDisplayCurrencyRate(currencyAmount.displayCurrencyCode, 1);
      }
    } else if (showUnconvertibleAmountAsZero) {
      // Sometimes a zero in display currency conveys useful info
      // even when the instrument has no face value and therefore no currency value
      return new Instrument({
        currencyCode: this.displayCurrencyCode,
        amount: 0,
        calculatedBy: this.calculatedBy,
      })
        .withIntl(this.intl)
        .withCurrenciesStaticResources(this.currenciesStaticResources)
        .withDisplayCurrencyRate(this.displayCurrencyCode, 1);
    }
    return null;
  }

  withAmountMatchedToCurrencyValueOf(
    otherInstrument: Instrument | null | undefined,
  ) {
    if (
      otherInstrument?.hasQuantity &&
      this.displayCurrencyCode &&
      this.displayCurrencyCode === otherInstrument?.displayCurrencyCode &&
      this.exchangeRate &&
      this.exchangeRate > 0 &&
      otherInstrument?.exchangeRate &&
      otherInstrument?.exchangeRate > 0
    ) {
      return this.withQuantity({
        amount:
          (otherInstrument.amount * otherInstrument.exchangeRate) /
          this.exchangeRate,
      });
    }
    return null;
  }

  formatInstrumentName(
    options: {
      fallbackName?: string;
      noFallback?: boolean;
    } = {},
  ) {
    const intl = this.intl;
    if (!intl) {
      return null;
    }
    return formatInstrumentName({
      ...options,
      instrumentDetails: this.instrumentDetails,
      currencyDetails: this.currencyDetails,
      intl,
    });
  }

  // Can also be used on contract IDs etc
  static truncateId(id?: string) {
    if (typeof id === 'string' && id.length > 14) {
      return id.slice(0, 8) + '…' + id.slice(-4);
    }
    return id;
  }

  formatInstrumentId({
    truncate,
  }: {
    truncate?: boolean;
  } = {}) {
    if (this.isDraft) {
      return undefined;
    }
    if (truncate) {
      return Instrument.truncateId(this.instrumentId);
    }
    return this.instrumentId;
  }

  formatInstrumentType({
    useCurrencyCodeForMoney,
  }: { useCurrencyCodeForMoney?: boolean } = {}) {
    const intl = this.intl;
    if (this.isMoney && useCurrencyCodeForMoney) {
      return this.currencyCode || null;
    }
    if (!intl || !this.instrumentType) {
      return null;
    }
    return intl.formatMessage(
      FORMAT_INSTRUMENT_TYPE[this.instrumentType] ||
        FORMAT_INSTRUMENT_TYPE_DEFAULT,
      { instrument_type: this.instrumentType },
    );
  }

  formatAmountUnits() {
    const intl = this.intl;
    if (this.isMoney) {
      return this.currencyCode || null;
    }
    if (!intl || !this.instrumentType) {
      return null;
    }
    return intl.formatMessage(
      FORMAT_TOKEN_UNITS[this.instrumentType] || FORMAT_TOKEN_UNITS_DEFAULT,
      { type: this.instrumentType, quantity: this.amount },
    );
  }

  formatIssuerName() {
    if (this.instrumentDetails) {
      return this.instrumentDetails.issuerName;
    }

    // For Instruments initialized from a currency code only,
    // we can use the “monetary authority” from the currency static data
    // as the issuer. Note that for proper CCY instruments with a contract
    // (digital currencies), the issuer is always taken from the contract data
    // instead (even if blank).
    if (this.isCurrency && this.currencyCode) {
      const resourceData = this.currenciesStaticResources?.get(
        this.currencyCode,
      );
      if (resourceData?.metadata?.monetaryAuthority) {
        return resourceData.metadata.monetaryAuthority;
      }
    }
  }

  get contractAddress() {
    return this.instrumentDetails?.contractAddress;
  }

  get contractActionFee(): Instrument | undefined {
    if (this.instrumentDetails || this.isBsv) {
      return new Instrument({
        instrumentId: CURRENCY_BSV,
        quantity: this.instrumentDetails?.contractFee || 0,
      });
    }
  }

  get instrumentTransferFee(): Instrument | undefined {
    if (this.instrumentDetails || this.isBsv) {
      return new Instrument({
        instrumentId:
          this.instrumentDetails?.transferFeeInstrumentId || CURRENCY_BSV,
        quantity: this.instrumentDetails?.transferFee || 0,
      });
    }
  }

  /** @internal */
  applyCustomNumberFormatOptions(
    number: number,
    formatOptions: TNumberFormatOptionsInternal,
    {
      enforceCompleteFractionGroups = false,
      increasePrecisionForTinyNumbers = false,
    }: TFormatAmountPartsOptions = {},
  ): TNumberFormatOptionsInternal {
    if (increasePrecisionForTinyNumbers && number) {
      if (
        Math.abs(number) < 0.000005 &&
        (formatOptions.maximumFractionDigits || 0) <= 6
      ) {
        formatOptions = {
          ...formatOptions,
          minimumFractionDigits: 9,
          maximumFractionDigits: 9,
        };
      } else if (
        Math.abs(number) < 0.005 &&
        (formatOptions.maximumFractionDigits || 0) <= 3
      ) {
        formatOptions = {
          ...formatOptions,
          minimumFractionDigits: 6,
          maximumFractionDigits: 6,
        };
      } else if (
        Math.abs(number) < 0.05 &&
        (formatOptions.maximumFractionDigits || 0) <= 2
      ) {
        formatOptions = {
          ...formatOptions,
          minimumFractionDigits: 3,
          maximumFractionDigits: 3,
        };
      }
    }

    if (enforceCompleteFractionGroups) {
      const parts = new Intl.NumberFormat(
        this.locale,
        formatOptions,
      ).formatToParts(number);
      let fractionDigitCount = parts.reduce(
        (count, { type, value }) =>
          type === 'fraction' ? count + value.length : count,
        0,
      );
      if (formatOptions.currency === CURRENCY_BSV) {
        fractionDigitCount = Math.max(0, fractionDigitCount - 2);
      }
      let digitCountForGroupsOf3 = Math.ceil(fractionDigitCount / 3) * 3;
      if (digitCountForGroupsOf3 !== fractionDigitCount) {
        if (formatOptions.currency === CURRENCY_BSV) {
          digitCountForGroupsOf3 = digitCountForGroupsOf3 + 2;
        }
        formatOptions = {
          ...formatOptions,
          minimumFractionDigits: digitCountForGroupsOf3,
          maximumFractionDigits: digitCountForGroupsOf3,
        };
      }
    }

    return formatOptions;
  }

  /** @internal */
  renderParts({
    number,
    parts,
    formatOptions,
    numberFormatOverrides,
    hideCurrency,
    showCurrencyCodeOnRight,
    allowCurrencySymbolAndCode,
    splitLongFractions = true,
    typographicMinusSigns = true,
    spaceAroundSigns = true,
    forceSpaceAroundCurrency = true,
    isBsv,
  }: {
    number: number;
    parts: (
      | Intl.NumberFormatPart
      | TFormatTokenQuantityPart
      | TFormatAmountExtendedPart
    )[];
    formatOptions: TNumberFormatOptionsInternal;
    numberFormatOverrides?: Intl.NumberFormatOptions;
    hideCurrency?: boolean;
    showCurrencyCodeOnRight?: boolean;
    allowCurrencySymbolAndCode?: boolean;
    splitLongFractions?: boolean;
    typographicMinusSigns?: boolean;
    spaceAroundSigns?: boolean;
    forceSpaceAroundCurrency?: boolean;
    isBsv?: boolean;
  }): (Intl.NumberFormatPart | TFormatAmountExtendedPart)[] {
    const currencyCode = formatOptions.currency;

    if (!hideCurrency && showCurrencyCodeOnRight && currencyCode) {
      if (
        !allowCurrencySymbolAndCode ||
        parts.some(
          ({ type, value }) => type === 'currency' && value === currencyCode,
        )
      ) {
        // If Intl.NumberFormat used the currency code, then replace it
        parts = new Intl.NumberFormat(this.locale, {
          ...formatOptions,
          style: 'decimal',
        }).formatToParts(number);
      }

      if (parts[parts.length - 1]?.type === 'currency') {
        // If Intl.NumberFormat put the currency symbol/code at
        // the end (as in France, Germany, etc) then just replace
        // it with the code (we don’t want e.g. '5,000 00 £GB GBP')
        parts[parts.length - 1].value = currencyCode;
      } else {
        parts = [
          ...parts,
          // Use a full-width space to separate the number from the currency code
          // (note that Intl.NumberFormat does the same thing in locales with
          // currency on the right)
          {
            type: 'space',
            value: spaceCharacter({ nonBreaking: true, narrow: false }),
          },
          { type: 'currency', value: currencyCode },
        ];
      }
    }

    let expandedParts: (Intl.NumberFormatPart | TFormatAmountExtendedPart)[] =
      parts.flatMap((part) => {
        // for convenience to cope with the output of formatMessage
        if (typeof part === 'string') {
          return { type: 'literal', value: part };
        }
        // expand inline formatted numbers (for tokens)
        if (part?.type === 'number') {
          return new Intl.NumberFormat(this.locale, {
            ...part.NumberFormatOptions,
            ...numberFormatOverrides,
          }).formatToParts(part.number);
        }
        return part;
      });

    // Character transformations
    expandedParts = expandedParts.map((part) => {
      let mapped: Intl.NumberFormatPart | TFormatAmountExtendedPart = {
        ...part,
      };

      // Identify whitespace
      if (mapped.type === 'literal' && matchWhitespace.test(mapped.value)) {
        mapped = {
          ...mapped,
          type: 'space',
        };
      }
      // Use typographical minus signs
      if (typographicMinusSigns && mapped.type === 'minusSign') {
        mapped.value = '−';
      }

      return mapped;
    });

    expandedParts = expandedParts.flatMap((part, index) => {
      if (splitLongFractions && part.type === 'fraction') {
        // For BSV fractions, align the groups to
        // the Satoshi amount: 1 Sat = 0.00 000 001 BSV
        // For all other numbers, use standard
        // milli, micro, nano, etc boundaries:
        // 1 billionth of a dollar = $0.000 000 001
        let groupDigitCount = isBsv ? 1 : 0;
        let currentGroup:
          | Intl.NumberFormatPart
          | TFormatAmountExtendedPart
          | null = null;
        let groupCount = 0;
        const result: (Intl.NumberFormatPart | TFormatAmountExtendedPart)[] =
          [];
        for (const digit of part.value) {
          if (groupDigitCount === 3) {
            result.push({
              type: 'space',
              value: spaceCharacter({ nonBreaking: true, narrow: true }),
            });
            currentGroup = null;
            groupDigitCount = 0;
          }
          if (currentGroup) {
            currentGroup.value += digit;
          } else {
            let type: 'fractionLarge' | 'fractionMedium' | 'fractionSmall';
            switch (groupCount) {
              case 0:
                type = 'fractionLarge';
                break;
              case 1:
                type = 'fractionMedium';
                break;
              default:
                type = 'fractionSmall';
                break;
            }
            currentGroup = { type, value: digit };
            result.push(currentGroup);
            groupCount += 1;
          }
          groupDigitCount += 1;
        }
        return result;
      }

      // Optionally add a narrow space to separate the currency symbol and/or
      // plus/minus sign from the start of the digits
      if (
        (spaceAroundSigns &&
          (part.type === 'minusSign' || part.type === 'plusSign')) ||
        (forceSpaceAroundCurrency && part.type === 'currency')
      ) {
        let paddedSign: (Intl.NumberFormatPart | TFormatAmountExtendedPart)[] =
          [part];
        if (
          expandedParts[index - 1] &&
          expandedParts[index - 1].type !== 'space'
        ) {
          paddedSign = [
            {
              type: 'space',
              value: spaceCharacter({ nonBreaking: true, narrow: true }),
            },
            ...paddedSign,
          ];
        }
        if (
          expandedParts[index + 1] &&
          expandedParts[index + 1].type !== 'space'
        ) {
          paddedSign = [
            ...paddedSign,
            {
              type: 'space',
              value: spaceCharacter({ nonBreaking: true, narrow: true }),
            },
          ];
        }
        return paddedSign;
      }

      return part;
    });

    let beforeNumber = true;
    return expandedParts.map((part, index, parts) => {
      switch (part.type) {
        case 'integer':
        case 'group':
        case 'decimal':
        case 'fraction':
        case 'plusSign':
        case 'minusSign':
          beforeNumber = false;
          break;
        case 'currency':
          // Calculate subType for currency parts
          if (currencyCode) {
            // let test = 5 301,26 £GB
            let subType;
            if (part.value === currencyCode) {
              subType = beforeNumber ? 'codeBefore' : 'codeAfter';
            } else {
              subType = beforeNumber ? 'symbolBefore' : 'symbolAfter';
            }
            return {
              ...part,
              subType,
            } as TFormatAmountExtendedPart;
          }
          break;
      }
      return part;
    });
  }

  formatAmountParts({
    isDelta,
    hideCurrency,
    showCurrencyCodeOnRight,
    allowCurrencySymbolAndCode,
    showDisplayCurrency,
    showDisplayCurrencyWhenSame,
    useCustomPrecisionForDisplayCurrency = false,
    increasePrecisionForTinyNumbers = false,
    showWhenZero = true,
    showUnconvertibleAmountAsZero,
    numberFormatOverrides,
    splitLongFractions = true,
    enforceCompleteFractionGroups = false,
    typographicMinusSigns = true,
    spaceAroundSigns = true,
    forceSpaceAroundCurrency = true,
  }: TFormatAmountPartsOptions = {}): TFormatAmountPartsResult | null {
    let asTokens: {
      number: number;
      formatted: string;
      parts: (
        | Intl.NumberFormatPart
        | TFormatTokenQuantityPart
        | TFormatAmountExtendedPart
      )[];
    } | null = null;

    if (!this.intl) {
      return null;
    }

    if (!this.isMoney) {
      // Provide formatted token counts for non-currency instruments
      let tokenMessage: MessageDescriptor = isDelta
        ? FORMAT_TOKEN_DELTA_DEFAULT
        : FORMAT_TOKEN_QUANTITY_DEFAULT;
      if (
        FORMAT_TOKEN_QUANTITY[this.instrumentType || INSTRUMENT_TYPE_UNKNOWN]
      ) {
        tokenMessage = (isDelta ? FORMAT_TOKEN_DELTA : FORMAT_TOKEN_QUANTITY)[
          this.instrumentType || INSTRUMENT_TYPE_UNKNOWN
        ];
      }
      asTokens = {
        number: this.quantity,
        formatted: this.intl.formatMessage(tokenMessage, {
          instrument_type: this.instrumentType || INSTRUMENT_TYPE_UNKNOWN,
          quantity: this.quantity,
          number: (formatted: TSdkIntlPart[]) => formatted,
          type: (formatted: TSdkIntlPart[]) => formatted,
        }) as string,
        parts: (
          this.intl.formatMessage(tokenMessage, {
            instrument_type: this.instrumentType || INSTRUMENT_TYPE_UNKNOWN,
            quantity: this.quantity,
            number: (formatted: TSdkIntlPart[]) => ({
              type: 'number',
              number: this.quantity,
              NumberFormatOptions: isDelta ? { signDisplay: 'always' } : {},
              value: flattenFormattedParts(formatted),
            }),
            type: (formatted: TSdkIntlPart[]) => ({
              type: 'tokenType',
              value: flattenFormattedParts(formatted),
            }),
          }) as (
            | string
            | TFormatTokenQuantityPart
            | TFormatAmountExtendedPart
          )[]
        ).map((part) =>
          typeof part === 'string' ? { type: 'literal', value: part } : part,
        ),
      };
    }

    const currencyAmount = this.toCurrencyAmount();
    let asInstrumentCurrency = null;
    if (currencyAmount?.currencyCode) {
      const precision = currencyAmount.precision;
      asInstrumentCurrency = {
        number: currencyAmount.amount,
        NumberFormatOptions: {
          style: 'currency' as Intl.NumberFormatOptions['style'],
          currency: currencyAmount.currencyCode,
          minimumFractionDigits: precision,
          maximumFractionDigits: precision,
        },
      };
    }

    const displayCurrencyAmount = this.toDisplayCurrencyAmount({
      showUnconvertibleAmountAsZero,
    });
    let asDisplayCurrency = null;
    if (displayCurrencyAmount?.currencyCode) {
      const precision =
        useCustomPrecisionForDisplayCurrency &&
        currencyAmount?.hasCustomPrecision &&
        !(this.isMoney && !this.isCurrency)
          ? currencyAmount.precision
          : displayCurrencyAmount.precision;
      asDisplayCurrency = {
        number: displayCurrencyAmount.amount,
        NumberFormatOptions: {
          style: 'currency' as Intl.NumberFormatOptions['style'],
          currency: displayCurrencyAmount.currencyCode,
          minimumFractionDigits: precision,
          maximumFractionDigits: precision,
        },
      };
    }

    const quantity = {
      tokens: asTokens,
      instrumentCurrency: asInstrumentCurrency,
      displayCurrency: asDisplayCurrency,
    };

    let formatOptions: TNumberFormatOptionsInternal = {};
    let parts: (
      | Intl.NumberFormatPart
      | TFormatTokenQuantityPart
      | TFormatAmountExtendedPart
    )[];
    let number = this.amount;

    if (showDisplayCurrency) {
      if (quantity?.displayCurrency) {
        if (
          !showDisplayCurrencyWhenSame &&
          !quantity?.tokens &&
          quantity?.displayCurrency?.NumberFormatOptions?.currency ===
            quantity?.instrumentCurrency?.NumberFormatOptions?.currency
        ) {
          return null;
        }
        number = quantity?.displayCurrency?.number;
        formatOptions = this.applyCustomNumberFormatOptions(
          number,
          {
            ...(hideCurrency
              ? {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }
              : {}),
            ...quantity?.displayCurrency?.NumberFormatOptions,
            ...(hideCurrency ? { style: 'decimal' } : {}),
            ...numberFormatOverrides,
          },
          {
            enforceCompleteFractionGroups,
            increasePrecisionForTinyNumbers,
          },
        );
        parts = new Intl.NumberFormat(this.locale, {
          ...formatOptions,
          ...formatOptions.overridesForFormat,
        }).formatToParts(number);
      } else {
        return null;
      }
    } else {
      if (quantity?.tokens) {
        // number = quantity?.tokens?.number;
        if (quantity?.tokens?.parts) {
          parts = quantity.tokens.parts;
          if (hideCurrency) {
            const numberPart = parts?.find?.(
              (part) => part.type === 'number',
            ) as TFormatTokenQuantityPart;
            if (!numberPart) {
              return null;
            }
            formatOptions = {
              ...numberPart.NumberFormatOptions,
              ...numberFormatOverrides,
            };
            parts = new Intl.NumberFormat(
              this.locale,
              formatOptions,
            ).formatToParts(number);
          }
        } else {
          return null;
        }
      } else if (quantity?.instrumentCurrency) {
        // number = quantity?.instrumentCurrency?.number;
        formatOptions = this.applyCustomNumberFormatOptions(
          number,
          {
            ...(hideCurrency
              ? {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }
              : {}),
            ...quantity?.instrumentCurrency?.NumberFormatOptions,
            ...(hideCurrency ? { style: 'decimal' } : {}),
            ...numberFormatOverrides,
          },
          {
            enforceCompleteFractionGroups,
            increasePrecisionForTinyNumbers,
          },
        );
        parts = new Intl.NumberFormat(this.locale, {
          ...formatOptions,
          ...formatOptions.overridesForFormat,
        }).formatToParts(number);
      } else {
        return null;
      }
    }
    if (!showWhenZero && !number) {
      return null;
    }
    const renderedParts = this.renderParts({
      number,
      parts,
      formatOptions,
      numberFormatOverrides,
      hideCurrency,
      showCurrencyCodeOnRight,
      allowCurrencySymbolAndCode,
      splitLongFractions,
      typographicMinusSigns,
      spaceAroundSigns,
      forceSpaceAroundCurrency,
      isBsv: formatOptions?.currency === CURRENCY_BSV,
    });

    return {
      parts: renderedParts,
      number,
      textOnly: renderedParts.map(({ value }) => value).join(''),
    };
  }

  formatSymbol({ forCurrencies = true, forNonCurrencies = true } = {}) {
    if (!this.hasDetails) {
      return null;
    }
    let parts;
    if (!this.hasQuantity) {
      parts = this.withQuantity({ amount: 0 }).formatAmountParts()?.parts;
    } else {
      parts = this.formatAmountParts()?.parts;
    }
    if (!parts) {
      return null;
    }
    const symbol = parts.find(
      ({ type }) =>
        (forCurrencies && type === 'currency') ||
        (forNonCurrencies && type === 'tokenType'),
    )?.value;
    return symbol || null;
  }

  parseEditableAmount(
    input: string | number,
    {
      customPrecisionFromAmount,
      roundAmountToQuantity,
    }: {
      customPrecisionFromAmount?: boolean;
      roundAmountToQuantity?: boolean;
    } = {},
  ) {
    if (!this.intl) {
      return input;
    }
    const parse = getNumberParseInfo(this.intl)?.parse;
    if (!parse) {
      return input;
    }
    const parsedNumber = parse(input);
    if (typeof parsedNumber !== 'number') {
      // This handles two cases:
      // - An empty string, as distinct from the number 0
      //   (.amount will return 0, but .hasQuantity will be false)
      // - An entry mistake that isn’t a valid number
      //   (the invalid string is preserved, so it can be corrected)
      const unparsed = this.withQuantity({});
      unparsed.#unparsedInput = parsedNumber;
      return unparsed;
    }
    return this.withQuantity({
      amount: parsedNumber,
      customPrecisionFromAmount,
      roundAmountToQuantity,
    });
  }

  /**
   * Use this where you want to test whether a
   * {@link Instrument.parseEditableAmount | parsed amount} is blank.
   * For example, if:
   *
   * - a number-entry field contains `"1.235oops!"`
   * - you’ve parsed that with
   *
   * ```js
   * value = "1.235oops!";
   * amount = instrument.parseEditableAmount(value, {roundAmountToQuantity: true});
   * ```
   *
   * Then:
   *
   * ```js
   * amount?.hasQuantity === false;
   * amount?.hasEditableAmount === true; // until the user clears the input
   * ```
   */
  get hasEditedAmount() {
    return !!this.formatEditableAmount();
  }

  formatEditableAmount(formatOptions?: TFormatAmountPartsOptions) {
    if (!this.hasQuantity) {
      if (typeof this.#unparsedInput !== 'string') {
        return '';
      }
      // To allow mistakes to be corrected
      return this.#unparsedInput;
    }
    return this.formatAmountParts({
      ...formatOptions,
      hideCurrency: true,
    })?.textOnly;
  }

  static formatEditableAmount(
    parsed: Instrument | null | undefined,
    formatOptions?: TFormatAmountPartsOptions,
  ) {
    if (!(parsed instanceof Instrument)) {
      return '';
    }
    return parsed.formatEditableAmount(formatOptions);
  }
}
